// Presentación pública de MiPAE: la portada de visitante (Home sin sesión) y
// la página «¿Qué es MiPAE?». Dominio puro, sin React, para poder fijarlo
// con tests.
//
// Por qué existe este archivo (2026-09-29): la identidad aprobada quedaba
// como un documento institucional largo y la portada no mostraba qué HACE
// MiPAE. La presentación se ordena ahora por MOMENTOS reconocibles de
// enfermería (entender un tema, fundamentar un diagnóstico, organizar el
// turno, consultar una escala o un cálculo), cada uno con las herramientas
// que existen hoy y un ejemplo del producto.
//
// Tres reglas que este archivo sostiene y sus tests fijan:
//   1. Los EJEMPLOS son ficticios y se rotulan así en pantalla, pero sus
//      números no se inventan: la pregunta de gases, el Braden y el goteo
//      salen de las mismas funciones de la app (quizReferencia,
//      escalasEvolucion, calculadorasTurno). Se copian aquí como literal
//      para no cargar esos módulos en la portada, y el test compara contra la
//      función real: si la app cambia un corte o una fórmula, la portada no
//      puede seguir mostrando el número viejo.
//   2. "Sin cuenta" / "Con cuenta" se deriva de lib/accessGate cuando la
//      herramienta está ahí (test), para no prometerle a un visitante algo
//      que le va a pedir iniciar sesión. MiPAE en turno, Benito y el
//      calendario no están en accessGate porque App.jsx los exige con sesión
//      y perfil completo; por eso llevan `requiereCuenta: true` explícito.
//   3. Los textos de identidad (misión, valores) NO se copian aquí: se leen
//      de constants/identidadMiPAE.js y se muestran literales. Este archivo
//      solo agrega cómo se nota cada valor en la app, siempre con funciones
//      que existen hoy.

import { requiereSesion } from "./accessGate";

// ─── Herramientas ────────────────────────────────────────────────────────────
// `acceso` es el id de lib/accessGate; `modulo` el color de identidad del
// módulo (Decisión H de docs/principios-diseno-v2.md: el color del módulo va
// en navegación y recursos de presentación, nunca en contenido clínico).
export const HERRAMIENTAS = {
  referencia: {
    nombre: "Enciclopedia",
    descripcion: "Laboratorio, gases, procedimientos y calculadoras.",
    modulo: "enciclopedia",
    acceso: "referencia",
  },
  estudio: {
    nombre: "Modo estudio",
    descripcion: "Preguntas para repasar y comprobar lo que sabes.",
    modulo: "enciclopedia",
    acceso: "referencia",
  },
  escalas: {
    nombre: "Escalas clínicas",
    descripcion: "Glasgow, Braden, Downton, RASS, CAM-ICU y más.",
    modulo: "escalas",
    acceso: "escalas",
  },
  medicamentos: {
    nombre: "Medicamentos",
    descripcion: "Fichas con dilución y compatibilidad de vías.",
    modulo: "medicamentos",
    acceso: "medicamentos",
  },
  "prepara-practica": {
    nombre: "Prepara tu práctica",
    descripcion: "Antes de tu primera rotación clínica.",
    modulo: "prepara-practica",
    acceso: "prepara-practica",
  },
  "prepara-internado": {
    nombre: "Prepara tu internado",
    descripcion: "Para preparar tu primer día.",
    modulo: "prepara-internado",
    acceso: "prepara-internado",
  },
  "prepara-turno": {
    nombre: "Prepara tu turno",
    descripcion: "Guía rápida antes de cada turno.",
    modulo: "prepara-turno",
    acceso: "prepara-turno",
  },
  // "Respira", como en la portada anterior: el ítem "Bienestar" repetía la
  // palabra de la sección (misma decisión que el menú, navBienestar.test).
  respira: {
    nombre: "Respira",
    descripcion: "Respiraciones y pausas guiadas.",
    modulo: "respira",
    acceso: "respira",
  },
  rapido: {
    nombre: "PAE Rápido",
    descripcion: "Diagnósticos NANDA con su razonamiento y plan NOC y NIC.",
    modulo: "pae-rapido",
    acceso: "rapido",
  },
  detallado: {
    nombre: "PAE Detallado",
    descripcion: "Valoración por sistemas y apoyo para formular diagnósticos.",
    modulo: "pae-detallado",
    acceso: "detallado",
  },
  turno: {
    nombre: "MiPAE en turno",
    descripcion: "Registro del turno, evolución por sistema y entrega.",
    modulo: "prepara-turno",
    requiereCuenta: true,
  },
  calendario: {
    nombre: "Calendario de turnos",
    descripcion: "Configuras tu rotación y MiPAE calcula tu semana.",
    modulo: "calendario",
    requiereCuenta: true,
  },
  benito: {
    nombre: "Benito",
    descripcion: "Responde dudas, abre herramientas y hace cálculos.",
    modulo: "pae-rapido",
    requiereCuenta: true,
  },
  revisa: {
    nombre: "Revisa",
    descripcion: "Dudas sobre cuidados, vigilancia y procedimientos.",
    modulo: "revisa",
    acceso: "revisa",
  },
  bitacora: {
    nombre: "Bitácora",
    descripcion: "Escribe sobre tu turno y sigue pensándolo.",
    modulo: "bitacora",
    acceso: "bitacora",
  },
  bibliografia: {
    nombre: "Biblioteca",
    descripcion: "Temas de estudio y referencias para tu práctica.",
    modulo: "bibliografia",
    acceso: "bibliografia",
  },
};

// ¿La herramienta pide cuenta? Fuente única: accessGate cuando la conoce.
export function herramientaRequiereCuenta(id) {
  const h = HERRAMIENTAS[id];
  if (!h) return true; // fail-safe: jamás prometer "sin cuenta" a ciegas
  if (h.requiereCuenta) return true;
  return requiereSesion(h.acceso);
}

// Orden del índice de la portada: primero lo que se abre ya, después lo que
// pide cuenta. Así el visitante ve que puede explorar antes de registrarse.
export const INDICE_SIN_CUENTA = ["escalas", "medicamentos", "referencia", "estudio", "prepara-practica", "prepara-internado", "prepara-turno", "respira"];
export const INDICE_CON_CUENTA = ["rapido", "detallado", "turno", "benito", "calendario", "revisa", "bitacora", "bibliografia"];

// ─── Momentos ────────────────────────────────────────────────────────────────
// `mision` es el tramo LITERAL de la misión que el momento hace concreto
// (ver tramosMision). Dos momentos comparten "mejorar su práctica".
export const MOMENTOS = [
  {
    id: "aprender",
    modulo: "enciclopedia",
    pestana: "Entender un tema",
    mision: "aprender",
    titulo: "Entender un tema antes de la práctica",
    situacion: "Mañana tienes práctica y quieres llegar con los gases arteriales claros.",
    texto: "La Enciclopedia reúne laboratorio, gases arteriales, vacunas, aislamiento y procedimientos paso a paso. Modo estudio te hace preguntas para comprobar lo que entendiste, y Benito te explica lo que quedó dando vueltas.",
    herramientas: ["referencia", "estudio", "benito"],
    vineta: "estudio",
  },
  {
    id: "fundamentar",
    modulo: "pae-rapido",
    pestana: "Fundamentar un diagnóstico",
    mision: "fundamentar sus decisiones",
    titulo: "Fundamentar un diagnóstico de enfermería",
    situacion: "Tienes un caso clínico y debes justificar por qué eliges un diagnóstico y no otro.",
    texto: "Con PAE Rápido partes de tu valoración. MiPAE sugiere diagnósticos NANDA con su relación, sus manifestaciones y el razonamiento clínico que los sostiene, y tú eliges cuáles trabajar. Después propone el plan NOC y NIC para que lo revises y lo ajustes.",
    herramientas: ["rapido", "detallado"],
    vineta: "pae",
  },
  {
    id: "turno",
    modulo: "prepara-turno",
    pestana: "Organizar mi turno",
    mision: "mejorar su práctica",
    titulo: "Organizar el registro del turno",
    situacion: "Recibes cuatro pacientes y quieres llegar a la entrega con la evolución al día.",
    texto: "MiPAE en turno sigue el orden real del turno: recibir, visitar, registrar lo que pasa, evolucionar y entregar. Escribes o dictas mientras ocurre, y MiPAE organiza esas notas por sistema para que revises la evolución y prepares la entrega. Al terminar el turno, el registro se borra.",
    nota: "Si eres TENS, tienes un registro propio de cuidados y observaciones.",
    herramientas: ["turno", "calendario"],
    vineta: "turno",
  },
  {
    id: "consultar",
    modulo: "escalas",
    pestana: "Consultar una escala o un cálculo",
    mision: "mejorar su práctica",
    titulo: "Consultar una escala, un fármaco o un cálculo",
    situacion: "Estás valorando a un paciente y necesitas el Braden, una dilución o el goteo de un suero.",
    texto: "Las escalas calculan el puntaje y lo clasifican. Las fichas de medicamentos reúnen presentaciones disponibles en Chile, dilución y compatibilidad de vías. Las calculadoras resuelven goteo, balance hídrico o infusiones con fórmulas, no con inteligencia artificial.",
    herramientas: ["escalas", "medicamentos", "referencia"],
    vineta: "consulta",
  },
];

// Qué momento abre la portada según la etapa que eligió el visitante. Sigue
// el protagonista por capa de lib/homeExplora (formación → PAE, ejercicio y
// TENS → MiPAE en turno). Sin etapa, el primero en el orden natural.
const MOMENTO_POR_ETAPA = { estudiante: "fundamentar", interno: "turno", enfermero: "turno", tens: "turno" };
export function momentoInicial(etapa) {
  return MOMENTO_POR_ETAPA[etapa] || MOMENTOS[0].id;
}

// ─── Misión conectada a los momentos ─────────────────────────────────────────
// Parte la misión LITERAL en tramos y marca los que corresponden a un
// momento. La concatenación de `texto` es SIEMPRE idéntica a la misión
// (test): la presentación subraya, jamás reescribe. Si un tramo no aparece
// (la misión cambió por decisión del fundador), se devuelve la misión entera
// sin marcas en vez de adivinar.
export const MARCAS_MISION = [
  { tramo: "aprender", momento: "aprender" },
  { tramo: "fundamentar sus decisiones", momento: "fundamentar" },
  { tramo: "mejorar su práctica", momento: "turno" },
];

export function tramosMision(mision, marcas = MARCAS_MISION) {
  const texto = String(mision || "");
  const tramos = [];
  let desde = 0;
  for (const { tramo, momento } of marcas) {
    const i = texto.indexOf(tramo, desde);
    if (i < 0) return [{ texto, momento: null }];
    if (i > desde) tramos.push({ texto: texto.slice(desde, i), momento: null });
    tramos.push({ texto: tramo, momento });
    desde = i + tramo.length;
  }
  if (desde < texto.length) tramos.push({ texto: texto.slice(desde), momento: null });
  return tramos;
}

// "claridad, rigor y confianza, … e innovación con enfermería": los nombres
// aprobados en minúscula dentro de la frase y la conjunción que pide el
// castellano ("e" ante i/hi). Sale de IDENTIDAD_MIPAE, no se copia.
export function fraseValores(nombres) {
  const n = nombres.map((x) => x.charAt(0).toLowerCase() + x.slice(1));
  if (n.length < 2) return n.join("");
  const ultimo = n[n.length - 1];
  const conj = /^h?i/i.test(ultimo) && !/^h?i[aeou]/i.test(ultimo) ? "e" : "y";
  return `${n.slice(0, -1).join(", ")} ${conj} ${ultimo}`;
}

// ─── Cómo se nota cada valor en la app ───────────────────────────────────────
// Solo funciones que existen hoy (verificadas contra el código el 2026-09-29).
// La descripción del valor se muestra literal desde identidadMiPAE.js; esto
// va debajo, separado y rotulado como "En MiPAE".
export const VALORES_EN_MIPAE = {
  "Claridad": "Cada herramienta dice qué hace y qué no. Benito, por ejemplo, recuerda que no reemplaza tu criterio ni la indicación del equipo.",
  "Rigor y confianza": "Los cálculos se hacen con fórmulas y no con inteligencia artificial, y lo que registras en MiPAE en turno se borra al terminar el turno.",
  "Empatía": "En Bienestar hay respiraciones y pausas guiadas para cuando las busques. MiPAE no te las pone como tarea.",
  "Autonomía profesional": "En el PAE, MiPAE sugiere diagnósticos con su razonamiento clínico y tú eliges cuáles trabajar. Benito aparece solo cuando lo llamas.",
  "Adaptabilidad": "El inicio y las herramientas cambian según tu etapa, y quienes trabajan como TENS tienen su propio registro de cuidados.",
  "Innovación con enfermería": "MiPAE lo creó un enfermero y cambia con lo que cuentan quienes lo usan. Desde la app puedes enviarnos tu opinión.",
};

// ─── Ejemplos ficticios ──────────────────────────────────────────────────────
// Cada uno se rotula "Ejemplo ficticio" en pantalla. Sus valores están
// fijados contra las funciones reales en presentacionMiPAE.test.js.
export const EJEMPLOS = {
  // Pregunta real de Modo estudio (quizReferencia, id gas-0) y la lectura que
  // hace el intérprete de gases de la Enciclopedia (gasesArteriales).
  estudio: {
    enunciado: "pH 7,28 · PaCO₂ 60 · HCO₃⁻ 26. ¿Qué trastorno ácido-base es?",
    opciones: ["Acidosis respiratoria", "Acidosis metabólica", "Alcalosis respiratoria", "Alcalosis metabólica", "Normal"],
    correcta: "Acidosis respiratoria",
    lectura: "Acidosis respiratoria · no compensada (agudo).",
    porque: "El pH está bajo y la PaCO₂ alta, con el HCO₃⁻ en rango.",
  },
  // El caso de demostración de la app (data/nanda DEMO_CASE) y su
  // diagnóstico de referencia. El factor relacionado queda por confirmar,
  // igual que en DemoPublica: no se completa con un dato supuesto.
  pae: {
    contexto: "Medicina interna",
    caso: "Paciente de 67 años con neumonía adquirida en la comunidad. Disnea leve, FR 24 rpm, SatO₂ 92% con cánula nasal a 2 L/min.",
    diagnostico: "Patrón respiratorio ineficaz",
    codigo: "00032",
    manifestadoPor: "Disnea y taquipnea, con FR de 24 rpm.",
    relacionadoCon: "Por confirmar al valorar a la paciente.",
    razonamiento: "La disnea y la frecuencia respiratoria registradas orientan este diagnóstico. El factor relacionado se confirma en la valoración y no se completa con un dato supuesto.",
    noc: { codigo: "0415", nombre: "Estado respiratorio" },
    nic: { codigo: "3350", nombre: "Monitorización respiratoria" },
  },
  // Registro del turno de un paciente ficticio. La evolución SOLO dice lo que
  // la cronología registró (la baja del dolor tiene su reevaluación) y no
  // imprime horas: la prosa generada no lleva la hora (norma 2026-08-02).
  turno: {
    contexto: "Cirugía",
    fases: ["Recibir", "Visitar", "Durante", "Evolucionar", "Entregar"],
    faseActual: "Evolucionar",
    cronologia: [
      { hora: "09:40", texto: "Curación de herida operatoria abdominal, sin signos de infección." },
      { hora: "11:05", texto: "Refiere dolor EVA 6/10 en herida operatoria. Se administra analgesia indicada." },
      { hora: "12:00", texto: "Reevaluación del dolor, EVA 2/10." },
      { hora: "12:30", texto: "Tolera régimen líquido, sin náuseas." },
    ],
    evolucion: [
      { sistema: "Neurológico", texto: "Dolor en herida operatoria EVA 6/10, que baja a EVA 2/10 tras la analgesia indicada." },
      { sistema: "Gastro-Metabólico", texto: "Tolera régimen líquido, sin náuseas." },
      { sistema: "Tegumentario · piel y heridas", texto: "Herida operatoria abdominal sin signos de infección. Se realiza curación." },
    ],
    // Una semana de cuarto turno (largo, noche, libre, libre), con los
    // círculos del calendario de la app (TiraSemana).
    semana: [
      { dia: "L", fecha: 5, turno: "largo" },
      { dia: "M", fecha: 6, turno: "noche" },
      { dia: "M", fecha: 7, turno: null },
      { dia: "J", fecha: 8, turno: null },
      { dia: "V", fecha: 9, turno: "largo" },
      { dia: "S", fecha: 10, turno: "noche" },
      { dia: "D", fecha: 11, turno: null },
    ],
  },
  // Lo que una enfermera dicta del mismo paciente y cómo queda la evolución
  // por sistemas (la de `turno`). El dictado dice todo lo que la evolución
  // afirma y nada más (test): la muestra no inventa un dato que nadie dictó.
  // La usa la invitación de MiPAE Enfermero (lib/invitacionMembresias.js).
  dictado: {
    contexto: "Cirugía",
    texto: "Hice la curación de la herida operatoria abdominal, sin signos de infección. Tenía dolor EVA 6 en la herida, le di la analgesia indicada y al reevaluar bajó a EVA 2. Tolera régimen líquido, sin náuseas.",
  },
  // El registro de un toque del TENS (tens/HojaCuidadoRapido.jsx) con
  // posiciones y cuidados de los catálogos reales (POSICIONES y CUIDADOS_TENS
  // de lib/tens.js, fijado por test).
  tens: {
    contexto: "Medicina",
    cama: "12",
    posicion: "Lateral izquierdo",
    cuidados: [
      { label: "Aseo bucal", hora: "14:50" },
      { label: "Lubricación de la piel", hora: "14:52" },
      { label: "Cambio de ropa de cama", hora: null },
    ],
  },
  // Resultado de la escala (escalasEvolucion.clasificarBradenTotal) y de la
  // calculadora "Goteo de suero" (calculadorasTurno.goteo).
  braden: { total: 13, label: "13/23", clasificacion: "Riesgo moderado", nivel: "moderado" },
  goteo: { volumen: 1000, horas: 8, mlHora: 125, gotasMin: 42 },
  // Títulos reales de las secciones de una ficha (fichas/DetalleFicha.jsx).
  fichaSecciones: ["Presentaciones disponibles en Chile", "Dilución EV", "Compatibilidad de vías", "Qué vigilar en enfermería", "Signos de alarma"],
  // Conversación ficticia con Benito. La respuesta nombra los seis ítems de
  // BRADEN_ITEMS y el rango de la escala (test).
  benito: {
    pregunta: "¿Qué evalúa la escala de Braden?",
    respuesta: "El riesgo de que una persona desarrolle una lesión por presión. Mira seis aspectos: percepción sensorial, humedad de la piel, actividad, movilidad, nutrición y fricción o cizallamiento. Va de 6 a 23 puntos, y mientras más bajo el puntaje, mayor es el riesgo.",
    accion: "Abrir la escala de Braden",
    // Mismo aviso que el pie del panel de Benito (CompaneroGlobal.jsx).
    aviso: "No reemplaza tu criterio ni la indicación del equipo. Sin nombres ni RUT.",
  },
};
